import { BodyEmailTemplate, FeatureKey, UserEmailTemplates, UserIntegration, UserSubscription } from '@numeo/types-core'

/**
 * Simplified Application type for extension use - only includes properties actually used by extension
 */
export interface ExtensionApplication {
    projectName: string
    features: Array<{
        feature: string
        enabled: boolean
    }>
    integrations: Record<string, unknown>
    settings: Record<string, unknown>
    subscription?: UserSubscription
    quotaUsage?: Record<FeatureKey, number | null> // Remaining quota for each feature (null = unlimited)
}

/**
 * Type definitions for extension communication
 */
export interface ExtensionUser {
    token: string
    userId?: string
    email?: string
    name?: string
    emailTemplates?: UserEmailTemplates | { bodyTemplates: BodyEmailTemplate[] | []; footer?: string }
    nylasEmails?: Array<{
        id: number | string
        email: string
        isPrimary: boolean
    }>
    integrations?: UserIntegration
    application?: ExtensionApplication | null
}

interface ExtensionAuthResponse {
    status: string
    message?: string
    isExistingUser?: boolean
    error?: string
}

// Chrome runtime error handling helper
function getChromeRuntimeError(): Error | null {
    if (chrome?.runtime && 'lastError' in chrome.runtime && chrome.runtime.lastError) {
        const error = new Error(chrome.runtime.lastError.message || 'Chrome runtime error')
        return error
    }
    return null
}

/**
 * Service to handle communication with the Numeo browser extension
 */
class ExtensionAuthService {
    private extensionId: string | null = null
    private connected = false
    private readonly storageKey = 'numeoExtensionId'

    constructor() {
        this.extensionId = localStorage.getItem(this.storageKey)
        this.setupExtensionAnnouncementListener()
    }

    private setupExtensionAnnouncementListener(): void {
        if (typeof window === 'undefined') return

        window.addEventListener('message', (event) => {
            if (event.origin !== window.location.origin) return

            if (event.data?.type === 'NUMEO_EXTENSION_INSTALLED' && event.data?.source === 'numeo-extension') {
                const extensionId = event.data.extensionId

                if (extensionId && typeof extensionId === 'string') {
                    // Store extension ID
                    this.setExtensionId(extensionId)
                    localStorage.setItem('numeo-extension-connected', 'true')

                    // If user is logged in, immediately sync auth via window.postMessage
                    const token = localStorage.getItem('accessToken')
                    if (token) {
                        window.dispatchEvent(new CustomEvent('extension-connected', { detail: { extensionId } }))
                    }
                }
            }
        })
    }

    /**
     * Initialize connection with the extension
     * @returns Promise that resolves when connection is established or rejects if extension is not found
     */
    async connect(): Promise<boolean> {
        if (!this.extensionId) {
            return false
        }

        if (!chrome?.runtime) {
            console.warn('Chrome runtime not available - extension sync disabled')
            return false
        }

        try {
            const response = await this.sendIdentityCheck()
            this.connected = response && response.status === 'success'

            if (!this.connected) {
                console.warn('Extension connection failed - removing stored extension ID')
                localStorage.removeItem(this.storageKey)
                this.extensionId = null
            } else {
                console.log('Successfully connected to extension')
            }

            return this.connected
        } catch (error) {
            console.error('Error connecting to extension:', error)
            localStorage.removeItem(this.storageKey)
            this.extensionId = null
            this.connected = false
            return false
        }
    }

    /**
     * Send an identity check message to the extension
     */
    private sendIdentityCheck(): Promise<ExtensionAuthResponse> {
        return new Promise((resolve, reject) => {
            if (!this.extensionId || !chrome?.runtime) {
                reject(new Error('Extension ID not available or Chrome runtime not found'))
                return
            }

            try {
                chrome.runtime.sendMessage(this.extensionId, { type: 'EXTENSION_ID_CHECK' } as Record<string, unknown>, (response: Record<string, unknown>) => {
                    const runtimeError = getChromeRuntimeError()
                    if (runtimeError) {
                        reject(new Error(runtimeError.message || 'Unknown error'))
                        return
                    }
                    resolve({
                        status: response.status as string,
                        message: response.message as string | undefined,
                        isExistingUser: response.isExistingUser as boolean | undefined,
                        error: response.error as string | undefined,
                    })
                })
            } catch (error) {
                reject(error)
            }
        })
    }

    /**
     * Send authentication data to the extension
     * @param user User data to send
     * @returns Promise that resolves with the extension's response
     */
    async syncAuthState(user: ExtensionUser | null): Promise<ExtensionAuthResponse | null> {
        // Early return if no extension ID is available
        if (!this.extensionId) {
            this.extensionId = localStorage.getItem(this.storageKey)
        }

        if (!this.extensionId) {
            console.warn('Extension ID not available - skipping sync')
            return {
                status: 'error',
                message: 'Extension ID not found. Please try again from the extension.',
            }
        }

        // Attempt connection if not already connected
        if (!this.connected) {
            const connected = await this.connect()
            if (!connected) {
                console.warn('Could not connect to extension - sync failed')
                return {
                    status: 'error',
                    message: 'Could not connect to extension.',
                }
            }
        }

        try {
            if (user) {
                localStorage.setItem('numeo-extension-connected', 'true')
                const result = await this.sendAuthMessage(user)
                console.log('Extension auth sync successful')
                return result
            } else {
                localStorage.removeItem('numeo-extension-connected')
                const result = await this.sendLogoutMessage()
                console.log('Extension logout sync successful')
                return result
            }
        } catch (error) {
            console.error('Extension sync failed:', error)
            this.connected = false
            return {
                status: 'error',
                message: 'Communication failed.',
            }
        }
    }

    /**
     * Send authentication message to the extension using window.postMessage
     */
    private sendAuthMessage(user: ExtensionUser): Promise<ExtensionAuthResponse> {
        return new Promise((resolve) => {
            // Add a unique identifier for the extension to recognize
            const message = {
                type: 'NUMEO_AUTH_SUCCESS',
                user: user,
                source: 'numeo-web', // helps the extension filter messages
            }
            window.postMessage(message, '*')
            // Since window.postMessage does not provide a direct response,
            // resolve immediately with a generic success response
            resolve({
                status: 'sent',
                message: 'Auth message sent via window.postMessage',
            })
        })
    }

    /**
     * Send logout message to the extension
     */
    private sendLogoutMessage(): Promise<ExtensionAuthResponse> {
        return new Promise((resolve, reject) => {
            if (!this.extensionId || !chrome?.runtime) {
                reject(new Error('Extension ID not available or Chrome runtime not found'))
                return
            }

            try {
                chrome.runtime.sendMessage(
                    this.extensionId,
                    {
                        type: 'DAT_AUTH_LOGOUT',
                        action: 'logout',
                    } as Record<string, unknown>,
                    (response: Record<string, unknown>) => {
                        const runtimeError = getChromeRuntimeError()
                        if (runtimeError) {
                            reject(new Error(runtimeError.message || 'Unknown error'))
                            return
                        }
                        resolve({
                            status: response.status as string,
                            message: response.message as string | undefined,
                            isExistingUser: response.isExistingUser as boolean | undefined,
                            error: response.error as string | undefined,
                        })
                    }
                )
            } catch (error) {
                reject(error)
            }
        })
    }

    /**
     * Store the extension ID for future use
     * @param extensionId Extension ID to store
     */
    setExtensionId(extensionId: string): void {
        this.extensionId = extensionId
        localStorage.setItem(this.storageKey, extensionId)
    }

    /**
     * Check if we're connected to the extension
     */
    isConnected(): boolean {
        return this.connected
    }

    /**
     * Get the stored extension ID
     */
    getExtensionId(): string | null {
        return this.extensionId
    }

    /**
     * Remove the stored extension ID
     */
    removeExtensionId(): void {
        localStorage.removeItem(this.storageKey)
        this.extensionId = null
    }
}

// Export as singleton
export const extensionAuthService = new ExtensionAuthService()
